// One sensitivity for a strip of sensor traces, as a clinical viewer keeps it. Every channel is
// drawn about its own mean over the run (the files keep raw DC, as dynamics' preview does), the
// group's largest deflection fits inside its lane so nothing clips, and traces past a millivolt (depth
// contacts during a seizure) read in mV. The scale bar is a round value tall enough to read.

/** Scale bar values: 1, 2, 2.5 and 5 × 10ⁿ. */
const STEPS = [1, 2, 2.5, 5];
/** Headroom over the largest deflection. */
const HEADROOM = 1.04;

/** The smallest round value (1, 2, 2.5 or 5 × 10ⁿ) at least `v`. */
export function roundUp(v: number): number {
  if (!(v > 0) || !Number.isFinite(v)) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  for (const s of STEPS) if (s * mag >= v * (1 - 1e-9)) return Number((s * mag).toPrecision(6));
  return Number((10 * mag).toPrecision(6));
}

export interface SensorScale {
  /** One trace per channel, about its mean, in `unit`. */
  traces: Float32Array[];
  unit: 'µV' | 'mV';
  /** Every lane spans ±`half`, in `unit`. */
  half: number;
  /** The scale bar, in `unit`. */
  bar: number;
}

/**
 * A group's traces and their one scale. `data` is `[sample][channel]` over `C` channels and
 * `toUv` converts its values to µV (1e6 for volts). `lanePx` is a lane's height on screen: the bar
 * is the smallest round value at least `minBarPx` tall there, and at least half the largest deflection.
 */
export function sensorScale(data: ArrayLike<number>, C: number, channels: number[], toUv: number, lanePx: number, minBarPx = 10): SensorScale {
  const n = C > 0 ? Math.floor(data.length / C) : 0;
  let peak = 0;
  const uv = channels.map((c) => {
    let mean = 0;
    for (let i = 0; i < n; i++) mean += data[i * C + c]!;
    mean = n ? mean / n : 0;
    const y = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      y[i] = (data[i * C + c]! - mean) * toUv;
      if (Math.abs(y[i]!) > peak) peak = Math.abs(y[i]!);
    }
    return y;
  });
  const mv = peak >= 1000;
  const k = mv ? 1e-3 : 1;
  const traces = mv ? uv.map((y) => y.map((v) => v * k)) : uv;
  const half = Math.max(peak * k, 1e-6) * HEADROOM;
  const perPx = lanePx > 0 ? (2 * half) / lanePx : 0;
  return { traces, unit: mv ? 'mV' : 'µV', half, bar: roundUp(Math.max(minBarPx * perPx, (peak * k) / 2)) };
}
